// Keeps a staff console signed in past its access token's lifetime.
//
// Access tokens live 15 minutes (`access_token_minutes`), and until this the
// consoles only ever called login: fifteen minutes into a session every API
// read 401'd - the member list, member detail, usage limits, deletion request
// - and the operator was dropped at the sign-in page with no explanation.
//
// This wraps fetch for every call to the API origin. An access token that has
// already expired is refreshed before the request goes; a request that still
// 401s triggers one refresh and one retry. Refreshes are single flight: the
// backend rotates the refresh token on every use, so two concurrent refreshes
// would present the same token twice and the second would fail, signing the
// operator out of a perfectly good session.

export type SessionTokens = {
  accessToken: string | undefined;
  refreshToken: string | undefined;
};

/** The `TokenPair` fields a refresh returns that the console keeps. */
export type RefreshedSession = {
  access_token: string;
  refresh_token: string;
  role: string;
  password_change_required?: boolean;
};

export type SessionFetchOptions = {
  apiBaseUrl: string;
  readTokens(): SessionTokens;
  /** Stores a refreshed pair. Returns false to refuse it (e.g. a role this console does not admit). */
  writeSession(session: RefreshedSession): boolean;
  /** Called once when a refresh fails: the session is over. */
  onSessionExpired(): void;
  isExpired(token: string): boolean;
  /** The underlying fetch. Read at call time so tests and other wrappers can replace it. */
  fetch?: typeof globalThis.fetch;
};

export type SessionFetch = {
  fetch: typeof globalThis.fetch;
  /** Refreshes now, sharing any refresh already in flight. Resolves to the new access token. */
  refresh(): Promise<string | undefined>;
};

const REFRESH_PATH = '/api/v1/auth/refresh';
// Paths that must never carry, or trigger a refresh of, the session's bearer.
const UNMANAGED_PATHS = new Set([REFRESH_PATH, '/api/v1/auth/login']);

/**
 * The URL a fetch input addresses, read by shape rather than `instanceof Request`:
 * a Request built by a different realm's constructor (a test DOM, a polyfilled
 * client) fails `instanceof` and would stringify to "[object Request]".
 */
function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

export function createSessionFetch(options: SessionFetchOptions): SessionFetch {
  const apiOrigin = new URL(options.apiBaseUrl);
  const apiPrefix = apiOrigin.pathname.replace(/\/$/, '');
  let inflight: Promise<string | undefined> | undefined;

  const baseFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    (options.fetch ?? globalThis.fetch)(input, init);

  function managed(url: URL): boolean {
    if (url.origin !== apiOrigin.origin) return false;
    const path = url.pathname.slice(apiPrefix.length);
    return !UNMANAGED_PATHS.has(path);
  }

  async function runRefresh(): Promise<string | undefined> {
    const { refreshToken } = options.readTokens();
    if (!refreshToken) return undefined;
    try {
      const response = await baseFetch(`${options.apiBaseUrl}${REFRESH_PATH}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: 'omit',
      });
      if (!response.ok) return undefined;
      const session = (await response.json()) as Partial<RefreshedSession>;
      if (
        typeof session.access_token !== 'string' ||
        typeof session.refresh_token !== 'string' ||
        typeof session.role !== 'string'
      ) {
        return undefined;
      }
      if (!options.writeSession(session as RefreshedSession)) return undefined;
      return session.access_token;
    } catch {
      return undefined;
    }
  }

  function refresh(): Promise<string | undefined> {
    inflight ??= runRefresh().then((token) => {
      inflight = undefined;
      if (!token) options.onSessionExpired();
      return token;
    });
    return inflight;
  }

  async function sessionFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // The URL is read without constructing a Request: constructing one from a
    // caller's Request takes its body, and forwarding that original afterwards
    // throws "Cannot construct a Request with a Request object that has already
    // been used" - which failed every sign-in, since the generated client sends
    // a Request and sign-in is never managed.
    const url = new URL(requestUrl(input), globalThis.location?.href);
    const tokens = options.readTokens();
    if (!managed(url) || (!tokens.accessToken && !tokens.refreshToken)) {
      // Untouched: forwarded exactly as the caller made it.
      return baseFetch(input, init);
    }
    const request = new Request(input, init);

    // Buffered once so the request can be sent a second time after a refresh.
    const body =
      request.method === 'GET' || request.method === 'HEAD'
        ? undefined
        : await request.arrayBuffer();
    const send = (token: string | undefined): Promise<Response> => {
      const headers = new Headers(request.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      else headers.delete('Authorization');
      return baseFetch(request.url, {
        method: request.method,
        headers,
        body,
        signal: request.signal,
        credentials: 'omit',
      });
    };

    let token = tokens.accessToken;
    if ((!token || options.isExpired(token)) && tokens.refreshToken) {
      token = await refresh();
      // The session is over; sending a token the API will reject only adds a
      // request. Callers see the same 401 they would have got.
      if (!token) return new Response(null, { status: 401 });
    }
    const response = await send(token);
    if (response.status !== 401 || !options.readTokens().refreshToken) return response;

    // Another request may already have rotated the session while this one
    // was in flight; retry with that rather than rotating again.
    const current = options.readTokens().accessToken;
    const retryToken = current && current !== token ? current : await refresh();
    if (!retryToken) return response;
    return send(retryToken);
  }

  return { fetch: sessionFetch as typeof globalThis.fetch, refresh };
}
